/**
 * One pooled THREE.Points system for every burst in the game (portal sparks,
 * incinerator embers, throw pops, landing dust). CPU-simulated, additive,
 * capped — dead particles collapse to the origin with zero size/alpha.
 */
import * as THREE from "three";

const MAX = 600;

interface P {
  life: number;
  maxLife: number;
  vx: number;
  vy: number;
  vz: number;
  gravity: number;
  r: number;
  g: number;
  b: number;
}

export class Particles {
  readonly points: THREE.Points;
  private readonly pos: Float32Array;
  private readonly col: Float32Array;
  private readonly meta: P[] = [];
  private cursor = 0;

  constructor() {
    this.pos = new Float32Array(MAX * 3);
    this.col = new Float32Array(MAX * 3);
    for (let i = 0; i < MAX; i++) {
      this.meta.push({ life: 0, maxLife: 1, vx: 0, vy: 0, vz: 0, gravity: 0, r: 0, g: 0, b: 0 });
      this.pos[i * 3 + 1] = -1000; // parked
    }
    const geo = new THREE.BufferGeometry();
    geo.setAttribute("position", new THREE.BufferAttribute(this.pos, 3));
    geo.setAttribute("color", new THREE.BufferAttribute(this.col, 3));
    const mat = new THREE.PointsMaterial({
      size: 0.14,
      vertexColors: true,
      transparent: true,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      sizeAttenuation: true,
    });
    this.points = new THREE.Points(geo, mat);
    this.points.frustumCulled = false;
  }

  burst(
    center: THREE.Vector3,
    color: THREE.Color | number,
    count: number,
    speed: number,
    opts: { spread?: number; gravity?: number; life?: number; up?: number } = {},
  ): void {
    const c = color instanceof THREE.Color ? color : new THREE.Color(color);
    const spread = opts.spread ?? 0.25;
    const gravity = opts.gravity ?? -6;
    const life = opts.life ?? 0.55;
    const up = opts.up ?? 0.4;
    for (let n = 0; n < count; n++) {
      const i = this.cursor;
      this.cursor = (this.cursor + 1) % MAX;
      const p = this.meta[i]!;
      p.life = p.maxLife = life * (0.6 + Math.random() * 0.7);
      const theta = Math.random() * Math.PI * 2;
      const s = speed * (0.4 + Math.random() * 0.9);
      p.vx = Math.cos(theta) * s;
      p.vz = Math.sin(theta) * s;
      p.vy = s * up + Math.random() * s * 0.6;
      p.gravity = gravity;
      p.r = c.r * (0.8 + Math.random() * 0.5);
      p.g = c.g * (0.8 + Math.random() * 0.5);
      p.b = c.b * (0.8 + Math.random() * 0.5);
      this.pos[i * 3] = center.x + (Math.random() - 0.5) * spread;
      this.pos[i * 3 + 1] = center.y + (Math.random() - 0.5) * spread;
      this.pos[i * 3 + 2] = center.z + (Math.random() - 0.5) * spread;
    }
  }

  update(dt: number): void {
    for (let i = 0; i < MAX; i++) {
      const p = this.meta[i]!;
      if (p.life <= 0) continue;
      p.life -= dt;
      if (p.life <= 0) {
        this.pos[i * 3 + 1] = -1000;
        this.col[i * 3] = this.col[i * 3 + 1] = this.col[i * 3 + 2] = 0;
        continue;
      }
      p.vy += p.gravity * dt;
      this.pos[i * 3] += p.vx * dt;
      this.pos[i * 3 + 1] += p.vy * dt;
      this.pos[i * 3 + 2] += p.vz * dt;
      const fade = p.life / p.maxLife;
      this.col[i * 3] = p.r * fade;
      this.col[i * 3 + 1] = p.g * fade;
      this.col[i * 3 + 2] = p.b * fade;
    }
    (this.points.geometry.getAttribute("position") as THREE.BufferAttribute).needsUpdate = true;
    (this.points.geometry.getAttribute("color") as THREE.BufferAttribute).needsUpdate = true;
  }
}
